/**
 * The frame: a left rail of destinations, a header saying who you are, and the
 * page. The rail's contents come from the surface, so the studio and the
 * portal are visibly different places rather than one app with a menu.
 */
import type { ReactNode } from "react";
import { Link, useRouter } from "../lib/router.tsx";
import { describeAccess, useAuth } from "../lib/auth.tsx";
import { SURFACE_NAMES, surface } from "../lib/surface.ts";

export interface NavItem {
  to: string;
  label: string;
  hint?: string;
}

export function Layout({ nav, children }: { nav: NavItem[]; children: ReactNode }) {
  const { user, signOut } = useAuth();
  const { path } = useRouter();

  return (
    <div className="shell">
      <aside className="rail">
        <div className="rail-brand">
          <span className="rail-mark" aria-hidden="true" />
          <span className="rail-name">{SURFACE_NAMES[surface]}</span>
        </div>
        <nav className="rail-nav">
          {nav.map((item) => {
            const active = item.to === "/" ? path === "/" : path.startsWith(item.to);
            return (
              <Link key={item.to} to={item.to} className={`rail-link${active ? " is-active" : ""}`}>
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="rail-foot">
          <a className="rail-cross" href={crossLink()}>
            {surface === "config" ? "Open the portal →" : "Open configuration →"}
          </a>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <div className="spacer" />
          {user && (
            <div className="row">
              <div className="whoami">
                <div className="whoami-name">{user.name}</div>
                <div className="whoami-role">{describeAccess(user)}</div>
              </div>
              <button className="btn btn-sm" type="button" onClick={() => void signOut()}>
                Sign out
              </button>
            </div>
          )}
        </header>
        <main className="page">{children}</main>
      </div>
    </div>
  );
}

/** The other surface, at the same site. In production that is a sibling
 *  hostname; in development it is the other dev-server port. */
function crossLink(): string {
  const { hostname, protocol, port } = window.location;
  const other = surface === "config" ? "portal" : "config";
  if (hostname.includes(".")) {
    return `${protocol}//${hostname.replace(/^[^.]+\./, `${other}.`)}`;
  }
  return `${protocol}//${hostname}:${other === "config" ? 5181 : 5180}`;
}
